import React from 'react';
import dayjs, { APP_TIMEZONE } from '@/shared/lib/dayjs';
import { AppButton } from '@/components/atoms';
import { Building, MapMarker, Memo, User } from '@/components/atoms/AppIcon';
import useAppModal from '@/hooks/useAppModal';
import { MASSAGER_JA } from '@/shared/i18n/massager';
import type { Visit, VisitServiceType, VisitStatus } from '@/types/massager/visit';

// Warning messages — strings extracted so the JSX block stays declarative
// (just a list of {show, text} pairs) and copy edits land in one place.
// Static keys = plain strings; dynamic keys = builder functions that
// interpolate the runtime value (km / date / date list).
// Two messages differ by service type (A2 鍼灸 / R2 マッサージ):
//   noActiveInsurance : A2 = 施術日, R2 = 訪問日
//   noConsentWarning  : A2 = 訪問日, R2 = 施術日
const buildWarningMessages = (serviceType: VisitServiceType) => {
  const isR2 = serviceType === 'R2';
  return {
    noActiveInsurance: isR2
      ? '訪問日に対応する保険証の登録がありません。'
      : '施術日に対応する保険証の登録がありません。',
    monthRezeptLocked: 'この受診者はすでに他の施術師にて登録されています。',
    requiresLateElderlySetting: '後期高齢を設定してください。',
    distanceWarning: '距離が測定できません。',
    distanceUnknownWarning:
      '訪問距離の自動測定で正しい測定が出来ませんでした。「詳細」→「距離編集」で表示される地図上で、正しい建物にピンを動かしてください。',
    noConsentWarning: isR2
      ? '訪問日に対応する同意書がありません。'
      : '施術日に対応する同意書がありません。',
    distancePrevError: '前の受診者が距離取得エラーのため、訪問距離の正しい測定ができませんでした。',
    distanceOrError:
      '拠点→受診者住所 及び 前受診者住所→受診者住所の２点間距離が正しく取得できませんでした。【詳細】→【距離編集】から、地図上の受診者のピンを移動し、正しい建物を特定してください。',
    distanceTooFarFromClinic: (km: number) => `施術所からの距離が離れすぎています。${km}km`,
    distancePrevTooFar: (km: number) => `距離が離れすぎています。${km}km`,
    consentCompletedEnd: (date: string) =>
      `${date}で同意書の終了にチェックが入っています。レセプト発行までに同意書期限の更新を行って下さい。`,
    consentExpiringEnd: (date: string) =>
      `${date}で同意書の期限が切れます。レセプト発行までに同意書期限の更新を行ってください。`,
    outcomeError: (date: string) => `${date}の施術が転帰になっています。`,
    distanceOver16Dates: (dates: string) => `16km以上の日付があります。（${dates}）`,
  };
};

const WARNING_TEXT_CLASS = 'text-sm font-semibold text-rose-600';

// Status-driven colours per BD spec — fixed, not theme-driven:
//   未訪問 (none)   → 青  (massager_started_at = null)
//   施術中 (now)    → 緑  (started_at ≠ null AND ended_at = null)
//   施術完了 (done) → 灰  (started_at ≠ null AND ended_at ≠ null)
const STATUS_STYLES: Record<
  VisitStatus,
  { border: string; accent: string; dot: string; button: string }
> = {
  none: {
    border: 'border-blue-400',
    accent: 'text-blue-600',
    dot: 'bg-blue-500',
    button: '!bg-blue-500 hover:!bg-blue-600 !border-blue-500 hover:!border-blue-600 !text-white',
  },
  now: {
    border: 'border-green-500',
    accent: 'text-green-600',
    dot: 'bg-green-500',
    button:
      '!bg-green-500 hover:!bg-green-600 !border-green-500 hover:!border-green-600 !text-white',
  },
  done: {
    border: 'border-gray-300',
    accent: 'text-gray-500',
    dot: 'bg-gray-400',
    button: '!bg-gray-400 hover:!bg-gray-500 !border-gray-400 hover:!border-gray-500 !text-white',
  },
};

export interface VisitCardProps {
  visit: Visit;
  /** Click handler for the memo (edit) icon — caller does the soft-delete pre-flight + navigate. */
  onMemoClick: () => void;
  onStart: (id: number) => void;
  onDone: (id: number) => void;
  onCancel: (id: number) => void;
  busy?: boolean;
}

function formatTime(iso: string | null): string {
  if (!iso) return '--:--';
  const d = dayjs(iso).tz(APP_TIMEZONE);
  return d.isValid() ? d.format('HH:mm') : '--:--';
}

const VisitCard: React.FC<VisitCardProps> = ({
  visit,
  onMemoClick,
  onStart,
  onDone,
  onCancel,
  busy = false,
}) => {
  const mapAddress = [visit.patient_address1, visit.patient_address2].filter(Boolean).join(' ');
  const mapUrl = `https://www.google.co.jp/maps?q=${encodeURIComponent(mapAddress)}`;
  const styles = STATUS_STYLES[visit.status];
  const appModal = useAppModal();

  const handleCancelClick = () => {
    appModal.confirm({
      type: 'error',
      title: MASSAGER_JA.confirm.cancel,
      okText: 'OK',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => onCancel(visit.id),
    });
  };

  const WARNING_MESSAGES = buildWarningMessages(visit.service_type);

  // Render-order list of all VisitCard warnings. Order = spec display
  // priority; mutual exclusion is enforced by the backend (e.g. only one
  // of the consent-state warnings can be non-null at a time).
  // Order mirrors VisitErrorChecker.collectMessages: [2] insurance → [5] lock →
  // [9] late-elderly → [11-16] distance group → [23] 16km other → [17-21] consent group.
  const warnings: ReadonlyArray<{ key: string; show: boolean; text: string }> = [
    // [2] insurance card
    {
      key: 'noActiveInsurance',
      show: !visit.has_active_insurance,
      text: WARNING_MESSAGES.noActiveInsurance,
    },
    // [5] month rezept locked
    {
      key: 'monthRezeptLocked',
      show: visit.is_month_rezept_locked,
      text: WARNING_MESSAGES.monthRezeptLocked,
    },
    // [9] late-elderly setting
    {
      key: 'requiresLateElderlySetting',
      show: visit.requires_late_elderly_setting,
      text: WARNING_MESSAGES.requiresLateElderlySetting,
    },
    // [11] prev visit distance error
    {
      key: 'distancePrevError',
      show: visit.has_distance_prev_error,
      text: WARNING_MESSAGES.distancePrevError,
    },
    // [12] distance not measured
    {
      key: 'distanceWarning',
      show: visit.has_distance_warning,
      text: WARNING_MESSAGES.distanceWarning,
    },
    // [13] clinic → patient >= 16 km
    {
      key: 'distanceTooFarFromClinic',
      show: visit.distance_too_far_km !== null,
      text: WARNING_MESSAGES.distanceTooFarFromClinic(visit.distance_too_far_km ?? 0),
    },
    // [14] prev patient → patient > 16 km
    {
      key: 'distancePrevTooFar',
      show: visit.distance_prev_too_far_km !== null,
      text: WARNING_MESSAGES.distancePrevTooFar(visit.distance_prev_too_far_km ?? 0),
    },
    // [15] distance resolution failed (non-ROOFTOP GPS)
    {
      key: 'distanceUnknownWarning',
      show: visit.has_distance_unknown_warning && !visit.has_distance_or_error,
      text: WARNING_MESSAGES.distanceUnknownWarning,
    },
    // [16] both-side latlon failure
    {
      key: 'distanceOrError',
      show: visit.has_distance_or_error,
      text: WARNING_MESSAGES.distanceOrError,
    },
    // [23] other dates in same month >= 16 km
    {
      key: 'distanceOver16Dates',
      show: visit.distance_over_16_dates !== null,
      text: WARNING_MESSAGES.distanceOver16Dates(visit.distance_over_16_dates ?? ''),
    },
    // [17/18] no consent / consent ended
    {
      key: 'noConsentWarning',
      show: visit.has_no_consent_warning,
      text: WARNING_MESSAGES.noConsentWarning,
    },
    {
      key: 'consentCompletedEnd',
      show: visit.consent_completed_end_date !== null,
      text: WARNING_MESSAGES.consentCompletedEnd(visit.consent_completed_end_date ?? ''),
    },
    // [19] consent expiring within month
    {
      key: 'consentExpiringEnd',
      show: visit.consent_expiring_end_date !== null,
      text: WARNING_MESSAGES.consentExpiringEnd(visit.consent_expiring_end_date ?? ''),
    },
    // [21] outcome registered in same consent term (A2 only)
    {
      key: 'outcomeError',
      show: visit.outcome_error_date !== null,
      text: WARNING_MESSAGES.outcomeError(visit.outcome_error_date ?? ''),
    },
  ];

  return (
    <div
      className={`flex h-full flex-col overflow-hidden rounded-lg border bg-white shadow-sm ${styles.border}`}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between gap-2 bg-blue-50 px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm sm:text-base">
          <span className={`inline-block h-2 w-2 rounded-full ${styles.dot}`} />
          <span className="font-semibold text-gray-900">
            {formatTime(visit.visit_datetime_from)} - {formatTime(visit.visit_datetime_to)}
          </span>
          <span className={`font-medium ${styles.accent}`}>{MASSAGER_JA.status[visit.status]}</span>
        </div>
        <AppButton
          type="primary"
          onClick={onMemoClick}
          aria-label={MASSAGER_JA.label.memo}
          icon={<Memo />}
          className="!text-sm "
          size="small"
        >
          編集
        </AppButton>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        {/* 施設 (clinic location) box */}
        {(visit.clinic_location_name || visit.clinic_location_address) && (
          <div className="flex items-start gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2.5 text-purple-700">
            <span className="mt-0.5 shrink-0">
              <Building />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5 text-sm">
              {visit.clinic_location_name && (
                <span className="font-semibold">{visit.clinic_location_name}</span>
              )}
              {visit.clinic_location_address && (
                <span className="break-all">{visit.clinic_location_address}</span>
              )}
            </div>
          </div>
        )}

        {/* Patient row */}
        <div className="flex items-start gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
            <User />
          </span>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-base font-semibold text-gray-900 sm:text-lg">
              {visit.patient_name ?? '—'}
              <span className="ml-0.5 text-sm font-normal text-gray-500">様</span>
            </span>
            {visit.consent_alert && visit.consent_term_to && (
              <span className="text-sm font-medium text-red-600">
                同意書期限：{dayjs(visit.consent_term_to).format('MM/DD')}
              </span>
            )}
            {visit.is_suddenly_visit && (
              <span className="text-sm font-medium text-red-600">突発的な往療</span>
            )}
          </div>
        </div>

        {/* Address row + map link below */}
        {visit.patient_address && (
          <div className="flex items-start gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center text-xl leading-none text-emerald-600">
              <MapMarker />
            </span>
            <div className="flex min-w-0 flex-1 items-start gap-2">
              <span className="break-all text-sm text-gray-700 flex-1">{visit.patient_address}</span>
              <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 rounded bg-emerald-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-600"
              >
                {MASSAGER_JA.action.mapLink}
              </a>
            </div>
          </div>
        )}

        {/* 特別地域加算 badge — indent to align with the text column after
            icons (icon h-7 w-7 = 28px + gap-2 8px = 36px = ml-9). */}
        {visit.is_special_area_addition && (
          <span className="ml-9 text-sm font-medium text-red-600">特別地域加算</span>
        )}

        {/* Warning list — data-driven. Each entry contributes at most one
            <span>; render order matches the spec's display priority. New
            warnings: append below. Text edits: see WARNING_MESSAGES. */}
        {warnings.map(w =>
          w.show ? (
            <span key={w.key} className={WARNING_TEXT_CLASS}>
              {w.text}
            </span>
          ) : null
        )}
      </div>

      {/* Actual treatment time (実績) — right-aligned, sits directly above
          the action button. から is shown as soon as start exists. */}
      {visit.massager_started_at && (
        <div className="px-4 pb-2 text-right text-sm text-gray-600">
          {MASSAGER_JA.label.actualTime} {formatTime(visit.massager_started_at)} から
          {visit.massager_ended_at ? ` ${formatTime(visit.massager_ended_at)}` : ''}
        </div>
      )}

      {/* Action button — sits inside the card padding so it has breathing
          room from the rounded bottom edge instead of bleeding to the border. */}
      <div className="px-4 pb-4">
        {visit.status === 'none' && (
          <AppButton
            type="primary"
            size="large"
            block
            disabled={busy}
            onClick={() => onStart(visit.id)}
            className={`!h-12 ${styles.button}`}
          >
            {MASSAGER_JA.action.start}
          </AppButton>
        )}
        {visit.status === 'now' && (
          <AppButton
            type="primary"
            size="large"
            block
            disabled={busy}
            onClick={() => onDone(visit.id)}
            className={`!h-12 ${styles.button}`}
          >
            {MASSAGER_JA.action.done}
          </AppButton>
        )}
        {visit.status === 'done' && (
          <AppButton
            type="primary"
            size="large"
            block
            disabled={busy}
            onClick={handleCancelClick}
            className={`!h-12 ${styles.button}`}
          >
            {MASSAGER_JA.action.cancel}
          </AppButton>
        )}
      </div>
    </div>
  );
};

export default VisitCard;
